import { BellRing, Coffee, Hand, LayoutDashboard, Plane, Sofa, TrainFront, Trees } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useLocale } from '@/i18n/provider';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';

/**
 * The iPhone and iPad app (App Store id 6811653240): LogPulse goes where you
 * go. Real screenshots from the app's sample session (the same ones as the
 * App Store listing), in English or Dutch to match the site.
 */
export const APP_STORE_URL = 'https://apps.apple.com/app/logpulse-logs-monitoring/id6811653240';

const PLACES: { key: string; icon: LucideIcon }[] = [
  { key: 'train', icon: TrainFront },
  { key: 'coffee', icon: Coffee },
  { key: 'couch', icon: Sofa },
  { key: 'park', icon: Trees },
  { key: 'plane', icon: Plane },
];

const POINTS: { key: string; icon: LucideIcon }[] = [
  { key: 'glance', icon: LayoutDashboard },
  { key: 'alerts', icon: BellRing },
  { key: 'act', icon: Hand },
];

/** Apple's official badge, in the visitor's language (Apple Marketing Toolbox). */
const BADGE_LANG: Record<string, string> = { en: 'en-us', nl: 'nl-nl', de: 'de-de', fr: 'fr-fr', es: 'es-es', it: 'it-it' };

function Phone({ src, alt, className }: { src: string; alt: string; className?: string }) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-[2.25rem] border-[7px] border-gray-950 bg-gray-950 shadow-2xl shadow-gray-900/30 dark:border-gray-800 dark:shadow-black/60',
        className,
      )}
    >
      <img src={src} alt={alt} width={600} height={1304} loading="lazy" className="block h-auto w-full" />
    </div>
  );
}

export function MobileAppSection() {
  const { t, locale } = useLocale();
  const shots = locale === 'nl' ? 'nl' : 'en';
  const shot = (name: string): string => `/images/ios-app/${shots}-${name}.webp`;
  const badge = `https://toolbox.marketingtools.apple.com/api/v2/badges/download-on-the-app-store/black/${BADGE_LANG[locale] ?? 'en-us'}`;

  return (
    <section
      className="relative overflow-hidden py-20 sm:py-28 bg-gradient-to-b from-white via-primary-50/60 to-white dark:from-gray-950 dark:via-primary-950/30 dark:to-gray-950"
      aria-labelledby="mobile-app-heading"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid gap-16 lg:grid-cols-2 lg:items-center">
        <div className="animate-on-scroll">
          <p className="eyebrow">{t('mobileApp.eyebrow')}</p>
          <h2
            id="mobile-app-heading"
            className="mt-4 font-display text-3xl sm:text-5xl font-bold tracking-tight text-gray-950 dark:text-white text-balance"
          >
            {t('mobileApp.title')}
          </h2>
          <p className="mt-5 text-lg text-gray-600 dark:text-gray-400">{t('mobileApp.subtitle')}</p>

          {/* Wherever you are. */}
          <ul className="mt-6 flex flex-wrap gap-2" aria-label={t('mobileApp.placesLabel')}>
            {PLACES.map(({ key, icon: Icon }) => (
              <li
                key={key}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white/80 px-3 py-1 text-sm text-gray-700 shadow-sm dark:border-gray-800 dark:bg-gray-900/70 dark:text-gray-300"
              >
                <Icon className="h-3.5 w-3.5 text-primary-600 dark:text-primary-400" aria-hidden />
                {t(`mobileApp.places.${key}`)}
              </li>
            ))}
          </ul>

          <ul className="mt-10 space-y-6">
            {POINTS.map(({ key, icon: Icon }) => (
              <li key={key} className="flex gap-4">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary-600/10 text-primary-700 dark:bg-primary-400/10 dark:text-primary-300">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <div>
                  <p className="font-semibold text-gray-950 dark:text-white">{t(`mobileApp.points.${key}.title`)}</p>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">{t(`mobileApp.points.${key}.body`)}</p>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener"
              onClick={() => trackEvent('app_store_click', { placement: 'home_mobile_app' })}
              className="inline-block transition-transform hover:-translate-y-0.5"
            >
              <img src={badge} alt={t('mobileApp.badgeAlt')} height={48} className="h-12 w-auto" />
            </a>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('mobileApp.note')}</p>
          </div>
        </div>

        {/* Three phones, and an alert that just came in. */}
        <div className="relative mx-auto w-full max-w-[34rem] animate-on-scroll" aria-hidden={false}>
          <div className="relative h-[30rem] sm:h-[38rem]">
            <Phone
              src={shot('2-alerts')}
              alt={t('mobileApp.shots.alerts')}
              className="absolute left-0 top-10 w-[42%] -rotate-6 opacity-95"
            />
            <Phone
              src={shot('4-datahealth')}
              alt={t('mobileApp.shots.dataHealth')}
              className="absolute right-0 top-10 w-[42%] rotate-6 opacity-95"
            />
            <Phone
              src={shot('1-overview')}
              alt={t('mobileApp.shots.overview')}
              className="absolute left-1/2 top-0 z-10 w-[50%] -translate-x-1/2"
            />
            <div className="mobile-app-ping absolute left-1/2 top-[38%] z-20 w-[78%] -translate-x-1/2 rounded-2xl border border-white/60 bg-white/85 p-3 shadow-xl backdrop-blur-md dark:border-gray-700/60 dark:bg-gray-900/85">
              <div className="flex items-start gap-3">
                <img src="/apple-touch-icon.png" alt="" className="mt-0.5 h-8 w-8 rounded-lg" />
                <div className="min-w-0">
                  <p className="flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-semibold uppercase tracking-wide">LogPulse</span>
                    <span>{t('mobileApp.ping.when')}</span>
                  </p>
                  <p className="text-sm font-semibold text-gray-950 dark:text-white sm:truncate">{t('mobileApp.ping.title')}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 sm:truncate">{t('mobileApp.ping.body')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
